import { motion } from "framer-motion";
import { Newspaper, Quote } from "lucide-react";
import annons from "@/assets/press/villanytt-annons.webp.asset.json";
import artikel1 from "@/assets/press/villanytt-artikel-1.webp.asset.json";
import artikel2 from "@/assets/press/villanytt-artikel-2.webp.asset.json";

/**
 * Press-sektion: BBN Nordic i Villanytt, december 2025.
 * Visar både redaktionellt reportage ("Glasklart och precis lagom varmt")
 * och annonsuppslaget. Bygger trovärdighet — tredjepartsmedia + Marcus Cavalli
 * citerad som expert.
 */
export function AsSeenInVillanytt() {
  return (
    <section className="relative py-20 md:py-28 bg-cream">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          {/* Vänster: rubrik + citat */}
          <div className="md:col-span-5 md:sticky md:top-28">
            <div className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white px-3 py-1.5 text-xs font-medium text-navy/80">
              <Newspaper size={14} className="text-gold" />
              Som sett i Villanytt · December 2025
            </div>
            <h2 className="mt-5 font-display text-3xl md:text-5xl text-navy leading-[1.05] tracking-tight">
              Marcus Cavalli, BBN Nordic, intervjuad som expert på solskyddsfilm.
            </h2>
            <p className="mt-5 text-base md:text-lg text-navy/75 leading-relaxed">
              I Villanytts decembernummer 2025 berättar vår grundare hur rätt
              film löser problem med hetta, bleknande inredning och bländning —
              med full bibehållen utsikt.
            </p>

            <figure className="mt-8 border-l-2 border-gold pl-5">
              <Quote className="text-gold mb-2" size={22} />
              <blockquote className="font-display italic text-navy text-lg md:text-xl leading-snug">
                "Fördelen med den nya generationen, särskilt 3M:s Prestige-serie
                som jag rekommenderar, är att filmen är helt genomskinlig och
                utsikten blir 100 procent glasklar."
              </blockquote>
              <figcaption className="mt-3 text-sm text-navy/70">
                — Marcus Cavalli, BBN Nordic, i Villanytt #12 / 2025
              </figcaption>
            </figure>
          </div>

          {/* Höger: bilder */}
          <div className="md:col-span-7 grid grid-cols-1 gap-5">
            <motion.a
              href={artikel1.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="block rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(15,32,68,0.35)] ring-1 ring-navy/10 bg-white"
            >
              <img
                src={artikel1.url}
                alt="Villanytt december 2025 — uppslag 'Glasklart och precis lagom varmt' om BBN Nordic"
                loading="lazy"
                decoding="async"
                width={720}
                height={459}
                sizes="(max-width: 768px) 100vw, 640px"
                className="w-full h-auto block aspect-[16/10] object-cover object-top"
              />

            </motion.a>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <motion.a
                href={artikel2.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="block rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(15,32,68,0.35)] ring-1 ring-navy/10 bg-white"
              >
                <img
                  src={artikel2.url}
                  alt="Villanytt-reportage — Marcus Cavalli förklarar 3M Prestige solfilm"
                  loading="lazy"
                  decoding="async"
                  width={480}
                  height={306}
                  sizes="(max-width: 768px) 100vw, 320px"
                  className="w-full h-auto block aspect-[4/5] object-cover object-top"
                />
              </motion.a>

              <motion.a
                href={annons.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="block rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(15,32,68,0.35)] ring-1 ring-navy/10 bg-white"
              >
                <img
                  src={annons.url}
                  alt="BBN Nordics annons i Villanytt december 2025 — Marknadens bästa solfilm"
                  loading="lazy"
                  width={634}
                  height={817}
                  className="w-full h-auto block aspect-[4/5] object-cover object-top"
                />
              </motion.a>
            </div>

            <p className="text-xs text-navy/75 mt-1">
              Publicerat i Villanytt nr 12 / 2025. Text: Eva Rydinger. Foto:
              @villaeden19.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
